/**
 * @file Drives the progress step of a multi-part Signify fetch: the operator's unsent candidate
 * picks, whether the insert should also write records, and the retry, cancel and new-list handlers.
 * Consumer: `SignifyFetchJobProgress`.
 * `picks` is a per-mount proxy, so closing the modal drops every unsent choice — nothing is sent
 * until the footer button fires, and one call carries the picks and the failed-part resets together
 * because the callable takes them together.
 * `onRetryPart` is the row's own button and deliberately sends NO picks: the operator pressing one
 * failed row has not asked for the choices they are still making elsewhere to be applied. Both
 * handlers share `retrying`, so a row press disables the footer and every other row while it runs.
 * `retrying` and `cancelling` stay separate leaves and never lock each other: `retrySignifyFetchJob`
 * re-reads `phase` transactionally because a cancel can land mid-call, and retrying a cancelled job
 * is the designed way to resume it.
 * "Also save as record" lives here rather than in the entry step: the record is written by the
 * insert, which happens after the fetch, so a flag chosen before Start would be lost with the step.
 * Everything the retry sends is read off the proxy before the await; the deletes afterwards are
 * writes, which is what makes them safe.
 */

import { useCallback, useContext, useRef, useState } from 'react';
import { proxy } from 'valtio';
import { MessageContext } from '@/providers/MessageContext';
import { UserContext } from '@/providers/UserContext';
import { uiState } from '@/store/uiState';
import { useCancelSignifyFetchJob, useRetrySignifyFetchJob } from '../../../api';
import { dismissedSignifyFetchJobs } from './services';
import type { SignifyFetchJobPicks } from '../index.types';

export const useSignifyFetchJobProgress = ({
  jobId,
  targetKey,
}: {
  jobId: string;
  targetKey: string;
}) => {
  const picksRef = useRef<SignifyFetchJobPicks | undefined>(undefined);
  if (!picksRef.current) {
    picksRef.current = proxy<SignifyFetchJobPicks>({});
  }
  const picks = picksRef.current;

  const { messageApi } = useContext(MessageContext);
  const { userRights } = useContext(UserContext);
  const [saveRecord, setSaveRecord] = useState(false);
  const { retrying, retryFetchJob } = useRetrySignifyFetchJob();
  const { cancelling, cancelFetchJob } = useCancelSignifyFetchJob();

  const onRetry = useCallback(async (): Promise<void> => {
    const sent = Object.entries(picks).map(([partId, code]) => ({ partId, code }));

    try {
      await retryFetchJob({ jobId, ...(sent.length > 0 ? { picks: sent } : {}) });
      sent.forEach(({ partId }) => {
        delete picks[partId];
      });
    } catch (error) {
      messageApi.error(
        error instanceof Error ? error.message : 'Failed to retry the Signify fetch.',
      );
    }
  }, [jobId, messageApi, picks, retryFetchJob]);

  const onRetryPart = useCallback(async (partId: string): Promise<void> => {
    try {
      await retryFetchJob({ jobId, partIds: [partId] });
    } catch (error) {
      messageApi.error(
        error instanceof Error ? error.message : 'Failed to retry the Signify fetch.',
      );
    }
  }, [jobId, messageApi, retryFetchJob]);

  const onCancel = useCallback(async (): Promise<void> => {
    try {
      await cancelFetchJob({ jobId });
    } catch (error) {
      messageApi.error(
        error instanceof Error ? error.message : 'Failed to cancel the Signify fetch.',
      );
    }
  }, [cancelFetchJob, jobId, messageApi]);

  /**
   * Stops nothing — the job is already finished or cancelled. It records the dismissal before
   * clearing the pointer, because the pointer lives in memory and reattach reads Firestore: without
   * the record, a reload would put the same finished list back in front of the operator.
   */
  const onNewList = useCallback((): void => {
    dismissedSignifyFetchJobs.add(jobId);
    delete uiState.signifyFetchJob.activeJobIdByTable[targetKey];
  }, [jobId, targetKey]);

  return {
    picks,
    saveRecord,
    canWriteRecords: Boolean(userRights?.recordsWrite),
    onSaveRecordChange: setSaveRecord,
    retrying,
    cancelling,
    onRetry,
    onRetryPart,
    onCancel,
    onNewList,
  };
};
